/**
 * Pagination — Reusable pagination controls.
 *
 * Renders prev/next arrows, page number buttons with ellipsis collapse,
 * and an optional "Showing X–Y of Z" summary. Designed to pair with
 * the usePagination hook but works with any page state.
 *
 * @example
 *   <Pagination
 *     page={page}
 *     totalPages={totalPages}
 *     onPageChange={setPage}
 *     totalItems={totalItems}
 *     startIndex={startIndex}
 *     endIndex={endIndex}
 *   />
 */
import React, { useMemo } from 'react';
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import ChevronsLeft from 'lucide-react/dist/esm/icons/chevrons-left';
import ChevronsRight from 'lucide-react/dist/esm/icons/chevrons-right';

export interface PaginationProps {
  /** Current 1-based page. */
  page: number;
  /** Total number of pages. */
  totalPages: number;
  /** Callback when page changes. */
  onPageChange: (page: number) => void;
  /** Total item count (for the summary line). */
  totalItems?: number;
  /** 0-based start index of current page (for summary). */
  startIndex?: number;
  /** 0-based end index (exclusive) of current page (for summary). */
  endIndex?: number;
  /** Max page buttons to show before collapsing to ellipsis. Default 5. */
  maxButtons?: number;
}

/**
 * Compute which page numbers to render, inserting null for ellipsis gaps.
 */
function getPageNumbers(
  page: number,
  totalPages: number,
  maxButtons: number,
): (number | null)[] {
  if (totalPages <= maxButtons) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | null)[] = [];
  const half = Math.floor((maxButtons - 2) / 2); // slots around current, excluding first/last
  let start = Math.max(2, page - half);
  let end = Math.min(totalPages - 1, page + half);

  // Shift window if it's too close to either edge
  if (page - half < 2) {
    end = Math.min(totalPages - 1, end + (2 - (page - half)));
  }
  if (page + half > totalPages - 1) {
    start = Math.max(2, start - (page + half - (totalPages - 1)));
  }

  pages.push(1);
  if (start > 2) pages.push(null);
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push(null);
  pages.push(totalPages);

  return pages;
}

const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  onPageChange,
  totalItems,
  startIndex,
  endIndex,
  maxButtons = 5,
}) => {
  const pageNumbers = useMemo(
    () => getPageNumbers(page, totalPages, maxButtons),
    [page, totalPages, maxButtons],
  );

  if (totalPages <= 1) return null;

  const showSummary =
    totalItems !== undefined &&
    startIndex !== undefined &&
    endIndex !== undefined;

  return (
    <div className="flex items-center justify-between pt-4">
      {/* Summary */}
      {showSummary ? (
        <span className="text-xs text-content-muted">
          {startIndex! + 1}–{endIndex} of {totalItems}
        </span>
      ) : (
        <span />
      )}

      {/* Controls */}
      <div className="flex items-center gap-1">
        {/* First page */}
        <button
          onClick={() => onPageChange(1)}
          disabled={page <= 1}
          className="p-1.5 rounded-lg text-content-tertiary hover:text-content-secondary hover:bg-surface-primary
                     disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent
                     transition-colors cursor-pointer border-none outline-none bg-transparent"
          aria-label="First page"
        >
          <ChevronsLeft size={14} />
        </button>

        {/* Prev */}
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="p-1.5 rounded-lg text-content-tertiary hover:text-content-secondary hover:bg-surface-primary
                     disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent
                     transition-colors cursor-pointer border-none outline-none bg-transparent"
          aria-label="Previous page"
        >
          <ChevronLeft size={14} />
        </button>

        {/* Page numbers */}
        {pageNumbers.map((num, idx) =>
          num === null ? (
            <span
              key={`ellipsis-${idx}`}
              className="px-1 text-xs text-content-faint select-none"
            >
              ...
            </span>
          ) : (
            <button
              key={num}
              onClick={() => onPageChange(num)}
              className={`
                min-w-[28px] h-7 rounded-lg text-xs font-medium transition-colors
                border-none outline-none cursor-pointer
                ${
                  num === page
                    ? 'bg-status-success/15 text-status-success'
                    : 'bg-transparent text-content-muted hover:text-content-secondary hover:bg-surface-primary'
                }
              `}
            >
              {num}
            </button>
          ),
        )}

        {/* Next */}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="p-1.5 rounded-lg text-content-tertiary hover:text-content-secondary hover:bg-surface-primary
                     disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent
                     transition-colors cursor-pointer border-none outline-none bg-transparent"
          aria-label="Next page"
        >
          <ChevronRight size={14} />
        </button>

        {/* Last page */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={page >= totalPages}
          className="p-1.5 rounded-lg text-content-tertiary hover:text-content-secondary hover:bg-surface-primary
                     disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent
                     transition-colors cursor-pointer border-none outline-none bg-transparent"
          aria-label="Last page"
        >
          <ChevronsRight size={14} />
        </button>
      </div>
    </div>
  );
};

export default React.memo(Pagination);
